import { round } from 'es-toolkit';
import { useState } from 'preact/hooks';
import AuthenticatedPage from '../../components/authenticated-page';
import { Link } from '../../components/link';
import { showModal } from '../../global-state/session';
import {
  useAccountUrl,
  useAdSkipsLeft,
  useSubscriptionStatus,
  useSubscriptionStore,
} from '../../global-state/usage-data';
import { getUser, useUser } from '../../global-state/user';
import { useUserDataStore } from '../../global-state/user-data';
import { APIError, requestApi } from '../../utils/api';
import debugLog from '../../utils/debug';
import { reportSentryError } from '../../utils/error-handling';
import { usePageTitle } from '../../utils/page-title';
import style from './style.module.scss';

export default function AccountPage() {
  usePageTitle('Account');
  const { isLoading: isLoadingAdSkips, description: adSkipText } = useAdSkipsLeft();
  const subscriptionStatus = useSubscriptionStatus();
  const subscriptionStore = formatSubscriptionStore(useSubscriptionStore() || '');
  const accountUrl = useAccountUrl();
  const user = useUser();
  const followedPodcastCount = useUserDataStore((s) => s.followedPodcastIds).length;

  const [isOpeningCustomerPoral, setOpeningCustomerPortal] = useState(false);

  return (
    <AuthenticatedPage>
      <div class={style.wrapper}>
        <h1>Account</h1>
        <p class="mb-2">
          Thanks for signing up for Adblock Podcast! If you have questions or
          suggestions to improve the app, please use the{' '}
          <button
            class="text-blue-500 underline"
            onClick={() => {
              () => showModal('feedback');
            }}
          >
            Feedback Form
          </button>{' '}
          or just{' '}
          <a
            class="text-blue-500 underline"
            href="mailto:adblockpodcast@gmail.com"
            target="_blank"
          >
            shoot me an email
          </a>
          .
        </p>
        <h2 className="mb-2 text-lg">Your Profile</h2>
        <ul class="list-disc pl-4">
          <li>Email: {user ? user.email : '?'} </li>
          <li>
            User ID:{' '}
            <code class="text-sm text-gray-600" style="user-select: all">
              {user ? user.id : '?'}
            </code>
          </li>
          <li>Currently following {followedPodcastCount} podcasts</li>
          {user && user.provider === 'email' && (
            <Link href="/update-password" class="text-blue-500 underline">
              Update Password
            </Link>
          )}
        </ul>
        {isLoadingAdSkips ? (
          <p className="mt-8">Loading...</p>
        ) : (
          <>
            <h2 className="mb-2 text-lg">Ad-Skips & Current Plan</h2>
            <ul class="list-disc pl-4">
              <li>{adSkipText}</li>
              <li>
                {subscriptionStatus ? (
                  <>
                    You have an {subscriptionStatus} subscription{' '}
                    {subscriptionStore
                      ? `through  ${
                          subscriptionStore.endsWith(' Store')
                            ? `the ${subscriptionStore}`
                            : subscriptionStore
                        }.`
                      : ''}
                  </>
                ) : (
                  <>
                    No paid subscription.{' '}
                    <Link href="/pricing" class="text-blue-500 underline">
                      See pricing plan(s) here
                    </Link>
                  </>
                )}
              </li>
            </ul>
            {accountUrl && (
              <p>
                <Link
                  onClick={
                    accountUrl === 'STRIPE_CUSTOMER_PORTAL'
                      ? () => {
                          setOpeningCustomerPortal(true);
                          redirectToCustomerPortalPage().finally(() =>
                            setOpeningCustomerPortal(false)
                          );
                        }
                      : undefined
                  }
                  href={
                    accountUrl !== 'STRIPE_CUSTOMER_PORTAL' ? accountUrl : undefined
                  }
                  class="cursor-pointer text-blue-500 underline"
                >
                  {isOpeningCustomerPoral
                    ? 'Redirecting...'
                    : 'View / update / cancel your subscription here'}
                </Link>
              </p>
            )}
            <h2 className="mb-2 text-lg">Delete Account</h2>
            {subscriptionStatus === 'active' ? (
              <p>
                First, cancel your paid subscription
                {accountUrl
                  ? ' by clicking the link above'
                  : ` through ${
                      subscriptionStore.endsWith(' Store')
                        ? `the ${subscriptionStore}`
                        : subscriptionStore
                    }`}
                . Then you can delete your account.
              </p>
            ) : (
              <>
                <p class={'mb-2'}>
                  Deleting your Adblock Podcast account will delete all of your user
                  metadata (e.g. followed podcasts, downloaded episodes, listening
                  history). This cannot be undone.
                </p>
                <p class={'mb-2'}>
                  We will continue to store a copy of your customer data (any
                  purchases), submitted feedback, and historical ad-skips for debugging
                  and record-keeping purposes.
                </p>
                <p class={'mb-2'}>
                  To permanently delete your Adblock Podcast account, click the button
                  below.
                </p>
                <button
                  onClick={() => showModal('delete-account')}
                  class="text-red-500 underline"
                >
                  Delete Account
                </button>
              </>
            )}
          </>
        )}
      </div>
    </AuthenticatedPage>
  );
}

async function redirectToCustomerPortalPage() {
  const log = (...args: any[]) => {
    debugLog({ logName: 'cp', logColor: 'chartreuse' }, ...args);
  };

  const { id: userId, email: userEmail, accessToken } = getUser() || {};

  interface ResponseData {
    data: string;
  }

  let url: ResponseData['data'];
  try {
    const startTime = Date.now();
    log('Requesting Customer Portal URL');
    const response = (
      await requestApi(
        'https://didgjhjnevdixtjltmho.functions.supabase.co/stripe-user-redirect',
        {
          action: 'customer-portal',
          userId,
          userEmail,
          env: location.origin.endsWith('www.adblockpodcast.com') ? 'prod' : 'test',
        },
        { Authorization: `Bearer ${accessToken}` }
      )
    ).json as ResponseData;
    if (!response.data) throw new APIError('Invalid response');
    url = response.data;
    const ellapsedMs = Date.now() - startTime;
    log(`Got Customer Portal URL in ${round(ellapsedMs / 1000, 1)}s. Redirecting...`);
  } catch (error) {
    reportSentryError(error as Error, {
      context: 'Failed to get Stripe Customer Portal URL',
    });
    return;
  }

  location.href = url;
}

function formatSubscriptionStore(str: string) {
  let i,
    frags = str.split('_');
  for (i = 0; i < frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }
  return frags.join(' ');
}
